import React, { useEffect, useState, useRef } from 'react';
import CommonIcon from '@/components/CommonIcon';
import cx from 'classnames';
import { useTranslations } from 'next-intl';
import { getSearchUrl } from '@/constants/url';
import { getSearchKeyWords, ISearchKeyWords } from '@/api/search';
import { useRouter } from 'next/router';
import Styles from './index.module.scss';

interface ISearchModal {
  isShow: boolean;  // 是否显示搜索框
  isShowSearchPush?: boolean;  // 是否显示搜索推荐弹窗(刚进入搜索结果页不显示)
  onClose: () => void;
}
const SearchModal = ({
  isShow,
  isShowSearchPush = true,
  onClose
}: ISearchModal) => {
  const router = useRouter();
  const { pathname } = useRouter();
  const t = useTranslations();
  const queryKeyword = typeof router.query.keyword === 'object' ? router.query.keyword?.[0] : router.query.keyword;
  // isShowSearchPush为false时，说明是刚进入搜索结果页,显示keyword;为true时，说明已经关闭过搜索框，再次打开时显示空字符串
  const [searchVal, setSearchVal] = useState<string>(isShowSearchPush ? '' : queryKeyword || '');
  const [searchKeyWords, setSearchKeyWords] = useState<ISearchKeyWords[] | []>([]);
  const [showPush, setShowPush] = useState<boolean>(isShowSearchPush && !searchVal.length);
  const [isFocus, setIsFocus] = useState<boolean>(isShowSearchPush);
  // 用于跟踪输入法组合输入状态
  const [isComposing, setIsComposing] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const pushRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(()=>{
    if(isShow) {
      getSearchKeyWords().then((res)=>{
        setSearchKeyWords(res);
      })
    }
  }, [isShow])

  useEffect(() => {
    const onClickHandler = (e: Event) => {
      const target = e.target as HTMLElement;
      const headerEle = headerRef.current;
      const PushEle = pushRef.current;

      // 当用户点击搜索框和推荐弹窗以外的区域时
      if (target !== headerEle && target !== PushEle && !headerEle?.contains(target) && !PushEle?.contains(target)) {
        if (pathname.includes('search')) {
          // 在搜索结果页面，只关闭推荐弹窗
          setShowPush(false);
        } else {
          // 不在搜索结果页面，关闭整个搜索模态框
          onClose();
        }
      }
    };
    const nextEle = document.querySelectorAll('#__next')?.[0];
    nextEle?.addEventListener('click', onClickHandler);

    return () => {
      nextEle?.removeEventListener('click', onClickHandler);
    }
  }, [pathname])

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
    setShowPush(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isComposing) {
      onSearch();
    }
  };

  const onSearch = (title?: string) => {
    inputRef.current?.blur();
    if(title) setSearchVal(title);
    setTimeout(()=>{
      setShowPush(false);
    }, 200)
    const searchRouter = getSearchUrl(title || searchVal)
    if (searchRouter) router.push(searchRouter);
    // 在搜索结果页每次搜索时先回到第一页
    if (pathname.includes('search')) {
      document.querySelectorAll('#__next')?.[0].scrollTo(0, 0);
    }
  };

  const onClear = () => {
    if (searchVal) {
      setSearchVal('');
      setShowPush(true);
      inputRef.current?.focus();
    } else {
      onClose();
    }
  }

  return (
    <div className={cx(Styles.searchModal, { [Styles.active]: isShow })}>
      <div className={cx(Styles.searchHeader, { [Styles.active]: isFocus })} ref={headerRef}>
        <div className={Styles.searchIcon} onClick={() => onSearch()}>
          <CommonIcon
            icon="header-search"
            className={Styles.icon}
          />
        </div>
        <input 
          ref={inputRef}
          type="text" 
          className={Styles.input} 
          onChange={onChangeSearch} 
          onKeyDown={onKeyDown}
          // 输入法组合输入开始时，设置 isComposing 为 true
          onCompositionStart={() => setIsComposing(true)}
          // 输入法组合输入结束时，设置 isComposing 为 false
          onCompositionEnd={() => setIsComposing(false)}
          value={searchVal}
          autoFocus={isShowSearchPush} // showPush为true说明不是刚进入搜索结果页
          onFocus={() => {
            setIsFocus(true)
            setShowPush(!searchVal.length)
          }}
          onBlur={() => {
            setIsFocus(false)
          }}
        />
        <div className={Styles.close} onClick={() => onClear()}>
          <CommonIcon
            icon="header-close"
            className={Styles.icon}
          />
        </div>
      </div>
      {
        searchKeyWords.length > 0 && showPush ? 
          <div className={Styles.searchPush} ref={pushRef}>
            <p className={Styles.pushTitle}>{t('search_recommend_hot')}</p>
            {
              searchKeyWords.slice(0, 20).map(({ link, title }, index) =>
                <div key={index} onClick={() => link ? window.location.href = link : onSearch(title)} className={Styles.pushItem}>
                  <span className={Styles.order}>{index + 1}</span>
                  <span className={Styles.text}>{title}</span>
                </div>
              )
            }
          </div> : null
      }
    </div>
  )
};

export default SearchModal;